import { Link } from "react-router-dom";
import useNoIndex from "@/hooks/useNoIndex";

export default function LicenseAgreementPage() {
  useNoIndex();

  const lastUpdated = "March 18, 2026";

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <Link to="/" className="text-sm text-teal-600 hover:text-teal-700">
          ← Back to home
        </Link>

        <h1 className="text-4xl md:text-5xl font-bold mt-4 mb-3">License Agreement</h1>
        <p className="text-slate-600 mb-10">Last updated: {lastUpdated}</p>

        <div className="space-y-8 text-slate-700 leading-7">
          <section>
            <p className="text-xl font-semibold text-slate-900">CreaVideos License Agreement</p>
            <p className="mt-3">
              This License Agreement (“Agreement”) governs the use of video templates and rendered
              videos available through CreaVideos.online (“CreaVideos”, “we”, “our”, or “us”).
            </p>
            <p className="mt-3">
              By using the service and downloading or rendering videos, you agree to this Agreement.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">1. Grant of License</h2>
            <p>
              Subject to an active subscription and compliance with the Terms of Service,
              CreaVideos grants you a:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>non-exclusive</li>
              <li>non-transferable</li>
              <li>non-sublicensable</li>
              <li>revocable</li>
            </ul>
            <p className="mt-3">license to use videos created using our templates.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">2. What You Can Do</h2>
            <p>You are allowed to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>use rendered videos for commercial and personal purposes</li>
              <li>
                use videos for:
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>social media (Instagram, Facebook, TikTok, etc.)</li>
                  <li>websites and landing pages</li>
                  <li>digital advertising (Meta Ads, Google Ads, etc.)</li>
                  <li>client work (if you are an agency or freelancer)</li>
                </ul>
              </li>
            </ul>
            <p className="mt-3">You may modify templates only through the CreaVideos platform.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">3. What You Cannot Do</h2>
            <p>You may NOT:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>resell, sublicense, share, or distribute templates</li>
              <li>provide templates as part of another platform or service</li>
              <li>redistribute templates in editable form</li>
              <li>claim ownership of templates</li>
              <li>extract assets to build a competing template library</li>
              <li>use templates to create a competing SaaS or product</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">4. View Limit and Extended Use</h2>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">Standard License</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Includes usage up to 500,000 total views</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-900 mt-4 mb-2">Enhanced License Required</h3>
            <p>You must contact us if:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>usage exceeds 500,000 views</li>
              <li>
                videos are used for:
                <ul className="list-disc pl-6 space-y-2 mt-2">
                  <li>TV</li>
                  <li>cinema</li>
                  <li>large-scale broadcast or streaming</li>
                </ul>
              </li>
            </ul>
            <p className="mt-3">
              <a className="text-teal-600 hover:text-teal-700" href="mailto:reno@creavideos.online">
                reno@creavideos.online
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">5. Ownership</h2>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>All templates remain the property of CreaVideos</li>
              <li>You own the final rendered videos, subject to this license</li>
              <li>No ownership is transferred for templates or platform technology</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">6. User Content</h2>
            <p>You retain ownership of content you upload (images, videos, text).</p>
            <p className="mt-3">You grant us a limited license to:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>process it</li>
              <li>render videos</li>
              <li>operate the service</li>
            </ul>
            <p className="mt-3">
              You are responsible for ensuring you have the rights to use your content.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">7. AI-Generated Content (PixVerse)</h2>
            <p>Videos may be generated using AI tools (e.g. PixVerse).</p>
            <p className="mt-3">You acknowledge:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>AI results may vary</li>
              <li>outputs may not be unique</li>
              <li>outputs may resemble other content</li>
            </ul>
            <p className="mt-3">CreaVideos does not guarantee:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>uniqueness</li>
              <li>copyright clearance</li>
              <li>suitability for all uses</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">8. Termination of License</h2>
            <p>Your license is valid only while your subscription is active.</p>
            <p className="mt-3">After cancellation:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>you may continue using previously downloaded videos</li>
              <li>you lose access to templates and editing tools</li>
            </ul>
            <p className="mt-3">If you violate this Agreement:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>your license may be revoked immediately</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">9. No Warranty</h2>
            <p>Templates and videos are provided “as is”.</p>
            <p className="mt-3">We do not guarantee:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>performance</li>
              <li>legal suitability</li>
              <li>platform acceptance (ads, social media, etc.)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">10. Limitation of Liability</h2>
            <p>CreaVideos is not liable for:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>misuse of videos</li>
              <li>copyright claims from user-uploaded content</li>
              <li>business losses</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-slate-900 mb-3">11. Contact</h2>
            <p>
              <a className="text-teal-600 hover:text-teal-700" href="mailto:reno@creavideos.online">
                reno@creavideos.online
              </a>
            </p>
            <p className="mt-2">
              <a className="text-teal-600 hover:text-teal-700" href="https://www.creavideos.online">
                https://www.creavideos.online
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
